import * as React from 'react';

import { calculatedBmi, formatBmi } from 'helper/bmi';
import i18n from 'helper/i18n';
import sanitize from 'helper/sanitizer';
import { template as compileTemplate } from 'helper/string';
import weightTargetValidatorFactory from 'helper/validator/weight-target';
import { getWeightForDisplay } from 'helper/weight';

import FieldInput from 'components/atoms/FieldInput';
import Text from 'components/atoms/Text';
import UIAsset from 'components/atoms/UIAsset';
import withValidation from 'components/hoc/withValidation';
import Bmi from 'components/molecules/Bmi';

import { ReduceWeightTargetType } from 'components/organisms/Anamnesis/Steps/DefineHealthTarget';
import { GenderKeyType } from '../../../types/GenderKey';
import { getImagePathFromGoal, TGoal } from '../HealthGoalSelection/HealthGoalSelection';
import { getHealthGoalDisplayStatus } from './get-display-status';

const ControlledInputWeightTarget = withValidation(FieldInput, weightTargetValidatorFactory, [
	sanitize.maxLengthWithDot(3, 2),
	sanitize.stripChars,
	sanitize.replaceCommasWithDots,
]);

export type HealthGoalItem = {
	name: TGoal;
	text: string;
};

type HealthGoalState = {
	error?: Object;
	hasValidInput?: boolean;
	inputState?: string;
	inputError?: string;
	inputValue?: string;
	showWarning?: boolean;
	mustLeaveCoach?: boolean;
	targetBmi?: number;
};

export type HealthGoalProps = {
	selectedGoal: TGoal;
	goals: HealthGoalItem[];
	text?: string;
	weight?: number;
	bmiValue: number;
	size?: number;
	isPregnant?: boolean;
	age: number | null;
	gender: GenderKeyType | null;
	reduceWeightTarget: ReduceWeightTargetType;
	onInputChange: Function;
	disabled?: boolean;
};

export const getRecommendedReduceWeightFromProps = (props: HealthGoalProps): string => {
	const value = props.reduceWeightTarget.recommendedReduceWeight;
	if (value === null || typeof value !== 'number' || !props.weight) {
		return '';
	}
	return getWeightForDisplay(parseFloat(props.weight as any) - value);
};

export default class HealthGoal extends React.Component<HealthGoalProps, HealthGoalState> {
	public static defaultProps = {
		size: 0,
		bmiValue: -1,
		weight: 0,
		isPregnant: false,
		reduceWeightTarget: {
			recommendedEnd: -1,
			recommendedStart: -1,
			recommendedReduceWeight: -1,
		},
	};

	constructor(props: HealthGoalProps) {
		super(props);

		this.state = {
			error: void 0,
			hasValidInput: false,
			inputState: void 0,
			inputError: void 0,
			showWarning: false,
			mustLeaveCoach: false,
			targetBmi: void 0,
			inputValue: getRecommendedReduceWeightFromProps(props),
		};
	}

	public componentDidUpdate(prevProps: HealthGoalProps) {
		const hasRecommendedReduceWeightChanged: boolean =
			prevProps.reduceWeightTarget.recommendedReduceWeight !==
			this.props.reduceWeightTarget.recommendedReduceWeight;
		const hasSelectedGoalChanged: boolean = Boolean(prevProps.selectedGoal !== this.props.selectedGoal);

		if (!hasRecommendedReduceWeightChanged && !hasSelectedGoalChanged) {
			return;
		}

		this.setState({
			error: void 0,
			hasValidInput: false,
			inputState: void 0,
			inputError: void 0,
			showWarning: false,
			mustLeaveCoach: false,
			targetBmi: void 0,
			inputValue: getRecommendedReduceWeightFromProps(this.props),
		});
	}

	public setInputValue = (value: string | number) => {
		this.setState({
			inputValue: value.toString(),
		});
		this.props.onInputChange.apply(null, [
			parseFloat((this.props.weight || 0).toString()) - parseFloat(value.toString()),
			this.state.hasValidInput,
		]);
	};

	public setValidInput = () => {
		this.setState({
			hasValidInput: true,
			inputState: void 0,
			inputError: void 0,
			showWarning: false,
			mustLeaveCoach: false,
		});
	};

	public setEmptyInput() {
		this.setState({
			hasValidInput: false,
			inputError: void 0,
			inputState: void 0,
			showWarning: false,
			mustLeaveCoach: false,
		});
	}

	public setInputWarning() {
		this.setState({
			inputState: void 0,
			inputError: void 0,
			hasValidInput: true,
			showWarning: true,
			mustLeaveCoach: false,
		});
	}

	public setInputError(message: string, mustLeave: boolean = false) {
		this.setState({
			inputState: 'error',
			inputError: message,
			mustLeaveCoach: mustLeave,
		});
	}

	get looseWeight(): number {
		if (!this.props.weight || !this.state.inputValue) {
			return 0;
		}
		return parseFloat(this.props.weight as any) - parseFloat(this.state.inputValue);
	}

	get targetBmi(): number {
		if (this.props.weight && this.props.size && typeof this.looseWeight === 'number') {
			return calculatedBmi(this.props.weight, this.props.size, this.looseWeight, true, true);
		}
		return -1;
	}

	public renderTargetText(): string {
		const label = i18n('react.impulsCoach.components.anamnesis.acceptHealthTarget.adjustWeightTarget');

		return compileTemplate(label, this.props.reduceWeightTarget);
	}

	public renderGettingFitTopText(bmi: number): string {
		const label = i18n('react.impulsCoach.components.anamnesis.acceptHealthTarget.gettingFitTarget.underweightTop');

		return compileTemplate(label, { bmi: formatBmi(bmi) });
	}

	public renderGettingFitPregnantTopText(bmi: number): string {
		const label = i18n('react.impulsCoach.components.anamnesis.acceptHealthTarget.gettingFitTarget.pregnantTop');

		return compileTemplate(label, { bmi: formatBmi(bmi) });
	}

	private get selectedGoalText() {
		let text = '';
		this.props.goals.every((goal) => {
			if (goal.name === this.props.selectedGoal) {
				text = goal.text;
				return false;
			}
			return true;
		});
		return text;
	}

	public render() {
		const { weight, size, bmiValue, isPregnant, disabled = false, age, gender } = this.props;
		const displayStatus = getHealthGoalDisplayStatus({
			isPregnant,
			bmiValue,
			selectedGoal: this.props.selectedGoal,
		});

		return (
			<div className="m-health-goal">
				<div className="m-health-goal__item m-health-goal__item--single">
					<div className="m-health-goal__bubble">
						<div className="m-health-goal__back" />
						<UIAsset
							className="m-health-goal__image"
							src={getImagePathFromGoal(this.props.selectedGoal)}
							alt={this.selectedGoalText}
						/>
					</div>
				</div>

				{displayStatus === 'pregnant' && (
					<div className="m-health-goal__lose-weight">
						<div className="m-health-goal__description m-rich">
							<p
								dangerouslySetInnerHTML={{
									__html: this.renderGettingFitPregnantTopText(bmiValue),
								}}
							/>
							<p
								dangerouslySetInnerHTML={{
									__html: i18n(
										'react.impulsCoach.components.anamnesis.acceptHealthTarget.gettingFitTarget.pregnantRelation'
									),
								}}
							/>
							<p
								dangerouslySetInnerHTML={{
									__html: i18n(
										'react.impulsCoach.components.anamnesis.acceptHealthTarget.gettingFitTarget.pregnantNotice'
									),
								}}
							/>
						</div>
					</div>
				)}

				{displayStatus === 'underweight' && (
					<div className="m-health-goal__lose-weight">
						<div className="m-health-goal__description m-rich">
							<p
								dangerouslySetInnerHTML={{
									__html: this.renderGettingFitTopText(bmiValue),
								}}
							/>
							<p
								dangerouslySetInnerHTML={{
									__html: i18n(
										'react.impulsCoach.components.anamnesis.acceptHealthTarget.gettingFitTarget.underweightRelation'
									),
								}}
							/>
							<p
								dangerouslySetInnerHTML={{
									__html: i18n(
										'react.impulsCoach.components.anamnesis.acceptHealthTarget.gettingFitTarget.underweightNotice'
									),
								}}
							/>
						</div>
					</div>
				)}

				{displayStatus === 'calculateBmi' && (
					<div className="m-health-goal__lose-weight">
						<div className="m-health-goal__description m-rich">
							<p
								dangerouslySetInnerHTML={{
									__html: this.renderTargetText(),
								}}
							/>
							<p
								dangerouslySetInnerHTML={{
									__html: i18n(
										'react.impulsCoach.components.anamnesis.acceptHealthTarget.adjustWeightConsequences'
									),
								}}
							/>
						</div>

						<div className="m-health-goal__target-text">
							<Text
								tag="p"
								size="xxl"
								textStyle="black"
								text={i18n('react.impulsCoach.components.HealthGoal.adjustWeightTitle')}
							/>
						</div>

						<div className="m-health-goal__description m-rich">
							<p>{i18n('react.impulsCoach.components.HealthGoal.addendum')}</p>
						</div>

						<ControlledInputWeightTarget
							id="weight"
							name="weigth"
							unit={i18n('react.impulsCoach.components.HealthGoal.weightUnit')}
							placeholder={i18n('react.impulsCoach.components.HealthGoal.weightPlaceholder')}
							value={this.state.inputValue ? this.state.inputValue.toString() : undefined}
							onChange={this.setInputValue as any}
							onValidationDone={this.setValidInput}
							validateOptions={{ targetBmi: this.targetBmi, weight: this.props.weight }}
							disabled={disabled}
						/>

						<div className="m-health-goal__bmi">
							{weight && size && (
								<Bmi
									weight={weight}
									size={size}
									age={age}
									gender={gender}
									target={this.props.reduceWeightTarget}
									inputValue={(
										parseFloat(weight.toString()) -
										parseFloat((this.state.inputValue || 0).toString())
									).toString()}
									onUpdate={() => {}}
								/>
							)}
						</div>
					</div>
				)}
			</div>
		);
	}
}
